import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import AnimatedStep from '../components/AnimatedStep';
import QuestionField from '../components/onboarding/QuestionField';

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const fetchForm = (token) =>
  axios.get(`${BASE_URL}/onboarding/public/${token}`).then((r) => r.data);

const saveProgress = (token, body) =>
  axios.post(`${BASE_URL}/onboarding/public/${token}/save`, body).then((r) => r.data);

const uploadFile = (token, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return axios
    .post(`${BASE_URL}/onboarding/public/${token}/upload`, fd)
    .then((r) => r.data);
};

const submitForm = (token, body) =>
  axios.post(`${BASE_URL}/onboarding/public/${token}/submit`, body).then((r) => r.data);

/* ── Confetti burst (CSS-only, no library) ── */
const CONFETTI_COLORS = ['#6366f1', '#818cf8', '#10b981', '#f59e0b', '#ec4899', '#38bdf8'];
function ConfettiBurst() {
  const pieces = Array.from({ length: 22 });
  return (
    <div className="ob-confetti-wrap" aria-hidden="true">
      {pieces.map((_, i) => (
        <div
          key={i}
          className="ob-confetti-piece"
          style={{
            left: `${Math.random() * 100}%`,
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animationDelay: `${Math.random() * 0.8}s`,
            animationDuration: `${1.4 + Math.random() * 1}s`,
            transform: `rotate(${Math.random() * 360}deg)`,
            width: `${6 + Math.random() * 6}px`,
            height: `${6 + Math.random() * 6}px`,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          }}
        />
      ))}
    </div>
  );
}

/* ── Progress dot stepper ── */
function ProgressStepper({ total, current }) {
  if (total <= 1) return null;
  const visibleTotal = Math.min(total, 10);
  const steps = Array.from({ length: visibleTotal }, (_, i) => i);
  return (
    <div className="ob-progress" role="progressbar" aria-valuenow={current + 1} aria-valuemax={total}>
      {steps.map((i) => {
        const isDone = i < current;
        const isActive = i === current;
        return (
          <div key={i} className="ob-progress-step">
            <div
              className={`ob-progress-dot ${isDone ? 'completed' : ''} ${isActive ? 'active' : ''}`}
              title={`Section ${i + 1}`}
            >
              {isDone ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                i + 1
              )}
            </div>
            <div className="ob-progress-line">
              <div
                className="ob-progress-line-fill"
                style={{ width: isDone ? '100%' : '0%' }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Loading skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="ob-page">
      <div className="ob-page-inner">
        <div className="ob-card">
          <div className="ob-skeleton ob-skeleton-line" style={{ width: '60px', height: '60px', borderRadius: '8px', marginBottom: '28px' }} />
          <div className="ob-skeleton ob-skeleton-line" style={{ width: '40%' }} />
          <div className="ob-skeleton ob-skeleton-line" style={{ width: '70%', marginBottom: '28px' }} />
          <div className="ob-skeleton ob-skeleton-block" />
          <div className="ob-skeleton ob-skeleton-block" />
          <div className="ob-skeleton ob-skeleton-line" style={{ width: '55%' }} />
        </div>
      </div>
    </div>
  );
}

/* ── Icon helpers ── */
const IconArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 8H3M7 4L3 8l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconWarning = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 3L18 17H2L10 3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M10 9v3M10 14h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconCheck = () => (
  <svg className="ob-check-svg" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 18l7 7 13-13"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconSaved = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconTick = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 7l3.5 3.5L11 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Splash Screen ── */
function SplashScreen({ templateName, staffName, loading, exiting, onBegin }) {
  return (
    <div className={`ob-splash${exiting ? ' ob-splash--exit' : ''}`}>
      <img src="/assets/logo.png" alt="Logo" className="ob-splash-logo" />

      <h1 className="ob-splash-title">
        Welcome to Your<br />Onboarding
      </h1>

      {staffName ? (
        <p className="ob-splash-sub">
          Hi <strong>{staffName}</strong>, everything you need to complete your onboarding is right here.
          It only takes about 10 minutes.
        </p>
      ) : (
        <p className="ob-splash-sub">
          Complete your onboarding to get started. It only takes about 10 minutes.
        </p>
      )}

      <div className="ob-splash-divider" />

      <button
        type="button"
        className="ob-btn-primary ob-splash-cta"
        onClick={onBegin}
        disabled={loading}
      >
        {loading ? (
          <>
            <span className="ob-spinner" style={{ borderTopColor: '#fff' }} />
            Loading your form...
          </>
        ) : (
          <>
            Begin Onboarding
            <IconArrowRight />
          </>
        )}
      </button>

      <p className="ob-splash-footer">Powered by The ST Group</p>
    </div>
  );
}

/* ══════════════════════════════════════════
   Main Component
══════════════════════════════════════════ */
function OnboardingForm() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [staffName, setStaffName] = useState('');
  const [template, setTemplate] = useState(null);
  const [invitation, setInvitation] = useState(null);

  const [showSplash, setShowSplash] = useState(true);
  const [splashExiting, setSplashExiting] = useState(false);

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingQId, setUploadingQId] = useState(null);
  const [showSaved, setShowSaved] = useState(false);
  const savedTimerRef = useRef(null);

  /* ── Load form ── */
  useEffect(() => {
    fetchForm(token)
      .then((res) => {
        if (res.completed) {
          setCompleted(true);
          setStaffName(res.staffName || '');
          setShowSplash(false);
          return;
        }
        setTemplate(res.template);
        setInvitation(res.invitation);
        const saved = {};
        (res.invitation?.answers || []).forEach((a) => {
          saved[a.questionId] = { value: a.value, fileUrl: a.fileUrl, fileName: a.fileName };
        });
        setAnswers(saved);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || 'Unable to load this onboarding form.');
        setShowSplash(false);
      })
      .finally(() => setLoading(false));
  }, [token]);

  /* ── Splash dismiss ── */
  const handleBegin = () => {
    setSplashExiting(true);
    setTimeout(() => {
      setShowSplash(false);
      setSplashExiting(false);
    }, 420);
  };

  /* ── Auto-save badge helper ── */
  const triggerSavedBadge = () => {
    setShowSaved(false);
    clearTimeout(savedTimerRef.current);
    // small delay so re-triggering resets the animation
    savedTimerRef.current = setTimeout(() => {
      setShowSaved(true);
      savedTimerRef.current = setTimeout(() => setShowSaved(false), 2600);
    }, 50);
  };

  /* ── Splash screen (shown on load, dismissed by user) ── */
  if (showSplash) {
    return (
      <div className="ob-page">
        <div className="ob-page-inner">
          <div className="ob-card">
            <SplashScreen
              templateName={template?.name}
              staffName={invitation?.staffName}
              loading={loading}
              exiting={splashExiting}
              onBegin={handleBegin}
            />
          </div>
        </div>
      </div>
    );
  }

  /* ── Error state ── */
  if (error) {
    return (
      <div className="ob-page">
        <div className="ob-page-inner">
          <div className="ob-card">
            <img src="/assets/logo.png" alt="Logo" className="ob-card-logo" />
            <div className="ob-state-card">
              <div className="ob-state-icon error">
                <IconWarning />
              </div>
              <h2 className="ob-state-title">Link Unavailable</h2>
              <p className="ob-state-desc">
                {error}
                <br />
                This link may have expired or is no longer valid. Please request a new invitation from your administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="ob-page">
        <div className="ob-page-inner">
          <div className="ob-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <ConfettiBurst />
            <img src="/assets/logo.png" alt="Logo" className="ob-card-logo" />
            <div className="ob-state-card">
              <div className="ob-state-icon success">
                <IconCheck />
              </div>
              <h2 className="ob-state-title">Onboarding Complete</h2>
              <p className="ob-state-desc">
                {staffName ? `Thank you, ${staffName}. ` : 'Thank you. '}
                Your onboarding has been submitted successfully. Our team will be in touch shortly.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sections = template?.sections || [];
  const section = sections[step];
  const isLast = step === sections.length - 1;

  if (!section) {
    return (
      <div className="ob-page">
        <div className="ob-page-inner">
          <div className="ob-card">
            <p style={{ color: 'var(--ob-muted)', textAlign: 'center' }}>
              No sections found in this template.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ── Helpers ── */
  const getVal = (qId) => answers[qId]?.value ?? null;
  const getFile = (qId) => answers[qId] ?? null;

  const setVal = (qId, value) => {
    setAnswers((prev) => ({ ...prev, [qId]: { ...prev[qId], value } }));
    if (errors[qId]) {
      setErrors((prev) => {
        const n = { ...prev };
        delete n[qId];
        return n;
      });
    }
  };

  const handleFileChange = async (qId, file) => {
    if (!file) {
      setAnswers((prev) => ({ ...prev, [qId]: { value: null, fileUrl: null, fileName: null } }));
      return;
    }
    setUploadingQId(qId);
    try {
      const res = await uploadFile(token, file);
      setAnswers((prev) => ({
        ...prev,
        [qId]: { value: res.fileUrl, fileUrl: res.fileUrl, fileName: res.fileName },
      }));
    } catch {
      /* silent */
    } finally {
      setUploadingQId(null);
    }
  };

  const validate = () => {
    const errs = {};
    (section.questions || []).forEach((q) => {
      if (!q.isRequired) return;
      const ans = answers[q._id];
      if (q.inputType === 'file') {
        if (!ans?.fileUrl) errs[q._id] = 'This document is required.';
      } else if (q.inputType === 'checkbox') {
        if (!Array.isArray(ans?.value) || ans.value.length === 0)
          errs[q._id] = 'Please select at least one option.';
      } else if (!ans?.value || String(ans.value).trim() === '') {
        errs[q._id] = 'This field is required.';
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildPayload = (sec) =>
    (sec?.questions || []).map((q) => ({
      sectionId: sec._id,
      questionId: q._id,
      fieldKey: q.fieldKey || '',
      value: answers[q._id]?.value ?? null,
      fileUrl: answers[q._id]?.fileUrl ?? null,
      fileName: answers[q._id]?.fileName ?? null,
    }));

  const handleNext = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await saveProgress(token, {
        answers: buildPayload(section),
        completedSectionId: section._id,
      });
      triggerSavedBadge();
    } catch {
      /* non-fatal */
    } finally {
      setSaving(false);
    }
    setDirection('forward');
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setDirection('backward');
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitForm(token, { answers: sections.flatMap(buildPayload) });
      setCompleted(true);
      setStaffName(invitation?.staffName || '');
    } catch (err) {
      setError(err?.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div className="ob-page">
      <div className="ob-page-inner">
        <div className="ob-card" style={{ position: 'relative' }}>

          {/* Auto-save badge */}
          {showSaved && (
            <div className="ob-autosave">
              <IconSaved />
              Progress saved
            </div>
          )}

          {/* Logo */}
          <img src="/assets/logo.png" alt="Logo" className="ob-card-logo" />

          {/* Progress stepper */}
          <ProgressStepper total={sections.length} current={step} />

          {/* Animated section content */}
          <AnimatedStep stepKey={section._id || `section-${step}`} direction={direction}>
            <div>
              {/* Section header */}
              <div className="ob-section-header">

                <h2 className="ob-section-title">{section.title}</h2>

                {section.description && (
                  <p className="ob-section-desc">{section.description}</p>
                )}
              </div>

              {/* Validation error banner */}
              {hasErrors && (
                <div className="ob-error-banner">
                  <IconWarning />
                  Please complete all required fields before continuing.
                </div>
              )}

              {/* Questions */}
              {(section.questions || []).map((q) => (
                <div key={q._id}>
                  <QuestionField
                    q={q}
                    value={getVal(q._id)}
                    onChange={(v) => setVal(q._id, v)}
                    fileMeta={getFile(q._id)}
                    onFileChange={(f) => handleFileChange(q._id, f)}
                    hasError={!!errors[q._id]}
                    uploading={uploadingQId === q._id}
                  />
                  {errors[q._id] && (
                    <div className="ob-field-error">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <circle cx="6" cy="6" r="5.25" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M6 4v3M6 8.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      {errors[q._id]}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </AnimatedStep>

          {/* Navigation */}
          <div className="ob-nav">
            {step > 0 ? (
              <button
                type="button"
                className="ob-btn-ghost"
                onClick={handleBack}
                disabled={saving || submitting || !!uploadingQId}
              >
                <IconArrowLeft />
                Back
              </button>
            ) : (
              <span />
            )}

            {!isLast ? (
              <button
                type="button"
                className="ob-btn-primary"
                onClick={handleNext}
                disabled={saving || submitting || !!uploadingQId}
              >
                {saving ? (
                  <>
                    <span className="ob-spinner" style={{ borderTopColor: '#fff' }} />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue
                    <IconArrowRight />
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                className="ob-btn-primary"
                onClick={handleSubmit}
                disabled={saving || submitting || !!uploadingQId}
              >
                {submitting ? (
                  <>
                    <span className="ob-spinner" style={{ borderTopColor: '#fff' }} />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit
                    <IconArrowRight />
                  </>
                )}
              </button>
            )}
          </div>

          {/* Footer note */}
          <p className="ob-footer-note">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 1v6M6 9h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Your progress is automatically saved when you continue.
          </p>
        </div>
      </div>
    </div>
  );
}

export default OnboardingForm;
